import { FAIcon, Progress, Skeleton } from '@/components'
import { format, PERMISSIONS, useFormWatch, usePermissions } from '@/utils'
import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { ResultOf } from 'gql.tada'
import type { UseFormReturn } from 'react-hook-form'
import { ABSENCE_TYPE_PERIOD_REPORT_QUERY } from '../queries'
import type { AbsenceForm } from './absence-dialog'
import {
  addDays,
  addHours,
  differenceInHours,
  startOfDay,
  startOfHour,
} from 'date-fns'

type AbsenceTypePeriodReport = NonNullable<
  ResultOf<typeof ABSENCE_TYPE_PERIOD_REPORT_QUERY>['absenceTypePeriodReport']
>

// Du har ingen timer igjen i fraværstype {name}.
// You have no hours left in absence type {name}.

function useValidateTimespan() {
  const translations = useTranslate({
    direction: 'absences.absence-dialog.report.validation.direction',

    noMoreAbsences: [
      'absences.absence-dialog.report.validation.no-more-absences',
      { name: <strong /> },
    ],

    maxContinuousHours: [
      'absences.absence-dialog.report.validation.max-continuous-hours',
      { name: <strong />, total: <strong />, hours: <strong /> },
    ],
    maxContinuousDays: [
      'absences.absence-dialog.report.validation.max-continuous-days',
      { name: <strong />, total: <strong />, days: <strong /> },
    ],

    notEnoughHours: [
      'absences.absence-dialog.report.validation.not-enough-hours',
      { name: <strong />, hours: <strong />, rem: <strong /> },
    ],
    notEnoughDays: [
      'absences.absence-dialog.report.validation.not-enough-days',
      { name: <strong />, days: <strong />, rem: <strong /> },
    ],
    noMoreHours: [
      'absences.absence-dialog.report.validation.no-more-hours',
      { name: <strong /> },
    ],
    noMoreDays: [
      'absences.absence-dialog.report.validation.no-more-days',
      { name: <strong /> },
    ],
  })

  return (
    report: AbsenceTypePeriodReport,
    start: Date,
    end: Date
  ): string | null => {
    if (start > end) return translations.direction

    const isDaily = report.absenceType.durationMode === 'DAILY'
    const absenceName = report.absenceType.name

    if (report.absencesRemaining !== null && report.absencesRemaining <= 0)
      return translations.noMoreAbsences({
        name: <strong>{absenceName}</strong>,
      })

    if (isDaily) {
      start = startOfDay(start)
      end = startOfDay(addDays(end, 1))
    } else {
      start = startOfHour(start)
      end = startOfHour(addHours(end, 1))
    }

    const diffHours = differenceInHours(end, start)
    if (
      !!report.absenceType.maxHoursContinuous &&
      diffHours > report.absenceType.maxHoursContinuous
    ) {
      if (isDaily)
        return translations.maxContinuousDays({
          name: <strong key="name">{absenceName}</strong>,
          days: <strong key="days">{Math.ceil(diffHours / 24)}</strong>,
          total: (
            <strong key="total">
              {Math.ceil(report.absenceType.maxHoursContinuous / 24)}
            </strong>
          ),
        })
      else
        return translations.maxContinuousHours({
          name: <strong key="name">{absenceName}</strong>,
          hours: <strong key="hours">{diffHours}</strong>,
          total: (
            <strong key="total">{report.absenceType.maxHoursContinuous}</strong>
          ),
        })
    }

    if (report.hoursRemaining !== null) {
      if (report.hoursRemaining <= 0) {
        if (isDaily)
          return translations.noMoreDays({
            name: <strong key="name">{absenceName}</strong>,
          })
        else
          return translations.noMoreHours({
            name: <strong key="name">{absenceName}</strong>,
          })
      }

      if (diffHours > report.hoursRemaining) {
        if (isDaily) {
          const daysRequested = Math.ceil(diffHours / 24)
          const daysAllowed = Math.floor(report.hoursRemaining / 24)

          return translations.notEnoughDays({
            name: <strong key="name">{absenceName}</strong>,
            days: <strong key="days">{daysRequested}</strong>,
            rem: <strong key="rem">{daysAllowed}</strong>,
          })
        } else {
          return translations.notEnoughHours({
            name: <strong key="name">{absenceName}</strong>,
            hours: <strong key="hours">{diffHours}</strong>,
            rem: <strong key="rem">{report.hoursRemaining}</strong>,
          })
        }
      }
    }

    return null
  }
}

interface ProgressInfo {
  value: number | null
  info: string
  warning: string | null
}

interface Props {
  form: UseFormReturn<AbsenceForm>
}

export function AbsencePeriodReport({ form }: Props) {
  const translations = useTranslate({
    period: 'absences.absence-dialog.report.period',

    used: {
      label: 'absences.absence-dialog.report.used.label',

      absences: [
        'absences.absence-dialog.report.used.absences',
        { used: <strong />, total: <strong /> },
      ],
      hours: [
        'absences.absence-dialog.report.used.hours',
        { used: <strong />, total: <strong /> },
      ],
      days: [
        'absences.absence-dialog.report.used.days',
        { used: <strong />, total: <strong /> },
      ],
    },
  })

  const { hasPermissions } = usePermissions()
  const canViewReport = hasPermissions(
    PERMISSIONS.timesheets.view.absenceperiodreport
  )

  const validateTimespan = useValidateTimespan()

  const datetimeStart = useFormWatch(form, 'datetimeStart')
  const datetimeEnd = useFormWatch(form, 'datetimeEnd')

  const queryVariables = {
    userId: useFormWatch(form, 'user'),
    absenceTypeId: useFormWatch(form, 'absenceType'),
    when: datetimeStart.toISOString(),
  }

  const enabled =
    canViewReport && !!queryVariables.userId && !!queryVariables.absenceTypeId

  const {
    data: reportData,
    loading: reportLoading,
    error: reportError,
  } = useQuery(ABSENCE_TYPE_PERIOD_REPORT_QUERY, {
    skip: !enabled,
    variables: queryVariables,
  })

  const report = reportData?.absenceTypePeriodReport ?? null

  if (!enabled || !!reportError) return null

  const progress = ((): ProgressInfo | null => {
    if (!report) return null

    let value: number | null = null
    let info: string = ''

    if (report.absencesMax === null || report.absencesRemaining === null) {
      if (report.absenceType.durationMode === 'DAILY')
        info = translations.used.days({
          used: <strong key="used">{Math.ceil(report.hoursUsed / 24)}</strong>,
          total: <strong key="total">{Math.ceil(report.hoursMax / 24)}</strong>,
        })
      else
        info = translations.used.hours({
          used: <strong key="used">{report.hoursUsed}</strong>,
          total: <strong key="total">{report.hoursMax}</strong>,
        })

      if (!!report.hoursMax)
        value = Math.min((report.hoursUsed / report.hoursMax) * 100, 100)
    } else {
      info = translations.used.absences({
        used: <strong key="used">{report.absencesTotal}</strong>,
        total: <strong key="total">{report.absencesMax}</strong>,
      })

      if (!!report.absencesMax)
        value = Math.min((report.absencesTotal / report.absencesMax) * 100, 100)
    }

    const warning = validateTimespan(report, datetimeStart, datetimeEnd)

    return {
      value,
      info,
      warning,
    }
  })()

  return (
    <div className="flex p-4 rounded-xl border border-gray-200 text-sm">
      {reportLoading || !report ? (
        <div className="w-full space-y-2">
          <Skeleton className="h-6 w-full rounded-md" />
          <Skeleton className="h-6 w-full rounded-md" />
        </div>
      ) : (
        <div className="w-full space-y-2">
          <div>
            <span className="font-semibold">{translations.period}</span>

            <span className="ml-2">
              {format(new Date(report.periodStart), 'PP')} -{' '}
              {format(new Date(report.periodEnd), 'PP')}
            </span>
          </div>

          {!!progress && (
            <div className="space-y-2">
              <div>
                <span className="font-semibold">{translations.used.label}</span>

                <span className="ml-2">{progress.info}</span>
              </div>

              {progress.value !== null && <Progress value={progress.value} />}

              {progress.warning && (
                <div className="flex gap-2 text-red-600">
                  <div>
                    <FAIcon icon="exclamation-triangle" type="solid" />
                  </div>
                  <span>{progress.warning}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
